import React, { useRef, useState } from "react";
import { CloudUploadIcon } from "../../components/icons";
import Toast from "../Toast/Toast";

const DEFAULT_EXTENSIONS = ["png", "jpeg", "jpg"];
const DEFAULT_MIME_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const DEFAULT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

const ProfilePicUpload = ({
  id,
  onFileUpload,
  heading,
  subHeading = "",
  hint,
  inputRef: externalInputRef,
  acceptedExtensions = DEFAULT_EXTENSIONS,
  acceptedMimeTypes = DEFAULT_MIME_TYPES,
  maxSizeBytes = DEFAULT_MAX_SIZE_BYTES,
  errorTitle = "Error",
  fileSizeErrorMessage = "File size should be less than 5MB",
  formatErrorMessage = "Please select a valid image format (PNG, JPEG, JPG).",
  uploadErrorMessage = "Failed to upload the selected image. Please try again.",
  disabled = false,
  className = "",
}) => {
  const internalInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);

  const assignInputRef = (node) => {
    internalInputRef.current = node;
    if (!externalInputRef) {
      return;
    }
    if (typeof externalInputRef === "function") {
      externalInputRef(node);
    } else {
      externalInputRef.current = node;
    }
  };

  const getInputElement = () =>
    externalInputRef && typeof externalInputRef !== "function"
      ? externalInputRef.current
      : internalInputRef.current;

  const uploadLabel = `${heading || ""} ${subHeading || ""}`.trim();
  const acceptAttribute = acceptedExtensions
    .map((extension) => `.${extension}`)
    .concat(acceptedMimeTypes)
    .join(",");

  const resetInput = (inputElement) => {
    if (inputElement) {
      inputElement.value = "";
    }
  };

  const processFile = (file, inputElement) => {
    if (!file || disabled || typeof onFileUpload !== "function") {
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    const isValidType = Boolean(
      extension && acceptedExtensions.includes(extension),
    );
    const isValidMime =
      !file.type || acceptedMimeTypes.includes(file.type.toLowerCase());

    if (!isValidType || !isValidMime) {
      Toast({
        type: "error",
        title: errorTitle,
        message: formatErrorMessage,
      });
      resetInput(inputElement);
      return;
    }

    if (file.size > maxSizeBytes) {
      Toast({
        type: "error",
        title: errorTitle,
        message: fileSizeErrorMessage,
      });
      resetInput(inputElement);
      return;
    }

    resetInput(inputElement);

    try {
      const result = onFileUpload(file);
      if (result != null && typeof result.then === "function") {
        void result.catch(() => {
          Toast({
            type: "error",
            title: errorTitle,
            message: uploadErrorMessage,
          });
        });
      }
    } catch {
      Toast({
        type: "error",
        title: errorTitle,
        message: uploadErrorMessage,
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    processFile(file, event.target);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setDragActive(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (disabled) {
      return;
    }
    const file = event.dataTransfer.files?.[0];
    if (!file) {
      return;
    }
    processFile(file, getInputElement());
  };

  const openFilePicker = () => {
    if (disabled) {
      return;
    }
    getInputElement()?.click();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <div
      className={`inline-flex items-center gap-3 w-fit max-w-full py-3 px-3.5 text-cfGlobalText bg-cfSecondary/40 rounded-lg border border-dashed border-cfCardBorder transition-colors focus:outline-none focus-visible:border-cfPrimaryButton focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 ${
        disabled
          ? "opacity-60 cursor-not-allowed"
          : "cursor-pointer hover:border-cfPrimaryButton/50 hover:bg-cfSecondary/70"
      } ${
        dragActive ? "border-cfPrimaryButton bg-cfPrimaryLight/30" : ""
      } ${className}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={openFilePicker}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={uploadLabel}
      aria-describedby={`${id}-dropzone-hint`}
      aria-disabled={disabled}
      id={`${id}-dropzone`}
    >
      <input
        type="file"
        accept={acceptAttribute}
        onChange={handleFileChange}
        className="hidden"
        id={id}
        ref={assignInputRef}
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
      <span className="shrink-0 flex items-center justify-center w-9 h-9 rounded-full bg-cfPrimaryLight/60 text-cfPrimaryButton">
        <CloudUploadIcon
          size={20}
          className="inline-flex"
          aria-hidden="true"
        />
      </span>
      <div className="flex flex-col min-w-0 text-left">
        <div className="text-sm font-semibold text-cfPrimaryButton whitespace-nowrap">
          {uploadLabel}
        </div>
        {hint ? (
          <div
            id={`${id}-dropzone-hint`}
            className="mt-0.5 text-xs text-cfGlobalText/80 whitespace-nowrap"
          >
            {hint}
          </div>
        ) : (
          <div id={`${id}-dropzone-hint`} className="sr-only">
            {uploadLabel}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePicUpload;
